import {createContext, useContext, useMemo} from 'react'

import {CellValidationContextProvider} from '../../../components/table/cell-validation'
import {
  RowMenuShortcutContext,
  RowMenuShortcutProvider,
  type RowMenuShortcutState,
} from '../../../components/table/state-providers/row-menu-shortcut-provider'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {buildTransitionalTableInstance} from '../helpers/table-api'
import type {Table, TableInstance, TableState} from '../helpers/types'
import {ColumnOrderDndProvider} from './columns/column-order-dnd-provider'

export type TableStateSelectedRowIdsContextValue = TableState['rowSelection']
type TableStateColumnResizingContextValue = TableState['columnSizing']
export type TableContextValue = Table<MemexItemModel>

export const TableStateSelectedRowIdsContext = createContext<TableStateSelectedRowIdsContextValue | null>(null)
const TableStateColumnResizingContext = createContext<TableStateColumnResizingContextValue | null>(null)
export const TableContext = createContext<TableContextValue | null>(null)

const TableCellHeightContext = createContext<number>(0)
/**
 * Sets up a context in which consumers can access the React Table instance
 *
 * @param props Props with a `table` attribute
 */
export const TableProvider: React.FC<{
  table: Table<MemexItemModel>
  cellHeight: number
  children: React.ReactNode
}> = props => {
  const state = props.table.getState()
  return (
    <ColumnOrderDndProvider>
      <TableStateSelectedRowIdsContext.Provider value={state.rowSelection}>
        <RowMenuShortcutProvider>
          <TableStateColumnResizingContext.Provider value={state.columnSizing}>
            <TableContext.Provider value={props.table}>
              <CellValidationContextProvider>
                <TableCellHeightContext.Provider value={props.cellHeight}>
                  {props.children}
                </TableCellHeightContext.Provider>
              </CellValidationContextProvider>
            </TableContext.Provider>
          </TableStateColumnResizingContext.Provider>
        </RowMenuShortcutProvider>
      </TableStateSelectedRowIdsContext.Provider>
    </ColumnOrderDndProvider>
  )
}

/**
 * Get the height of table cells
 */
export function useTableCellHeight(): number {
  const cellHeight = useContext(TableCellHeightContext)
  return cellHeight
}

/**
 * Get a reference to a transitional table instance object.
 *
 * The return type is a subset of the properties available on a
 * v7 `TableInstance`; this is a temporary hook to ease the transition
 * to React Table v8 which introduces a new table API.
 * Tracking: https://github.com/github/memex/issues/20940
 *
 * @link [V8 Table API docs](https://tanstack.com/table/v8/docs/api/core/table)
 *
 * Caution: Be careful with this hook—our table instance never changes, so table
 * state updates will not cause consumers of this hook to re-render, for
 * example.
 */
export function useTableInstance(sourceTable?: Table<MemexItemModel>): TableInstance<MemexItemModel> {
  const table = useContext(TableContext) ?? sourceTable

  if (!table) {
    throw new Error('Must use `useTableInstance` in child of `<TableProvider> or provide `sourceTable` prop')
  }

  // Create a stable table instance object that remains referentially equal
  // as long as the source table is referentially equal.
  const memoTableInstance = useMemo(() => buildTransitionalTableInstance(table), [table])
  // Use Object.assign to merge the latest values onto this stable object
  // This maintains referential equality checks for consumers of this hook
  return Object.assign(memoTableInstance, buildTransitionalTableInstance(table))
}

/**
 * Get the table's row menu shortcut origin.
 */
export function useTableRowMenuShortcutOrigin(): RowMenuShortcutState['rowMenuShortcutOrigin'] {
  const rowMenuContext = useContext(RowMenuShortcutContext)
  return rowMenuContext?.rowMenuState?.rowMenuShortcutOrigin
}

/**
 * Get the table's internal column resizing state.
 */
export function useTableColumnResizing(): TableStateColumnResizingContextValue {
  const columnResizing = useContext(TableStateColumnResizingContext)

  if (!columnResizing) {
    throw new Error('Must use `useTableColumnResizing` in child of `<TableProvider>')
  }

  return columnResizing
}

try{ TableStateSelectedRowIdsContext.displayName ||= 'TableStateSelectedRowIdsContext' } catch {}
try{ TableStateColumnResizingContext.displayName ||= 'TableStateColumnResizingContext' } catch {}
try{ TableContext.displayName ||= 'TableContext' } catch {}
try{ TableCellHeightContext.displayName ||= 'TableCellHeightContext' } catch {}